// @givmo/board — EVERY word CBG-RC11's screens add, in one place: each board approves its minutes its
// own way (D-CBG-26). The board's rule, recorded on Meeting rules, decides the one way for a set of
// minutes (the backend serves it on the minutes read, `approval_way`; backend `board_minutes.md`
// §A1–§A15), and every page here words that way, never re-deriving it.
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them. Jonathan approved the frames for building on 2026-10-12 22:52
// PDT ("looks good"), and the words in copy round CBG-RC11-COPY1 on 2026-10-13 21:51 PDT
// ("approve"; `scratchpad/2026-10-13-rc11-copy-round/copy-review.html`), with the changes agreed at
// 21:44: the officer's dialog says the one record-not-decisions line, and m7's line that a recused
// director "steps out for that vote" is gone; and, approved at 22:40 PDT, the stepped-out signer's
// line, the withdrawn way's "When and why" without "always", and the closed minutes' consent note.
// Each line names the frame that draws it (`g1`–`g4`, `m1`–`m7`, `d4`, `d4b`; the design branch
// `design/board-minutes-approval-closed-sessions` @ `8d5c8ee8`), or "not drawn" for a state no
// frame shows (the orchestrator's C5, C6 and C11 among them, approved on the same page). The
// fallbacks a missing fact reaches (no date recorded, a meeting's read under way or failed) were
// approved in copy round CBG-RC11-COPY2 (Jonathan, 2026-10-14 19:32 PDT). RC9's "Approve by written
// consent" lives where it always did, `../consents/minutes-consent-words.ts`, approved.
//
// ⚠ Plan §3.2 and §3.4 bind every line: what the board's rule is and what Board does, never what the
// law requires, never "must" or "should". A person is named in full, as the minutes record them, never
// by a first name (the orchestrator's C8). No pronoun for a named person.

import { type MinutesNaming, namedMeetingDate } from '../consents/minutes-consent-words';
import { joinWords } from '../roster/officer-roles';

/** The ways a board's two rules record (backend `BoardGovernanceParameterChoice`). */
export type RuleWay =
  | 'written_consent'
  | 'next_meeting'
  | 'officer'
  | 'same_way'
  | 'directors_present'
  | 'later_meeting';

/** The record-not-decisions line, said where minutes are approved (the orchestrator, 15:37 PDT). */
const RECORD_NOT_DECISIONS =
  'Approving minutes approves the record of what happened, not the decisions in it again.';

/** "Givmo starts from this way…", said only while the board hasn't recorded its own rule. */
const GIVMO_STARTS_FROM =
  'Givmo starts from this way until your board records its own, under Governance, Meeting rules.';

/** Every RC11 line: approved in CBG-RC11-COPY1 (2026-10-13 21:51 PDT), its fallbacks in COPY2. */
export const minutesRuleWords = {
  // ── The ways, by name (Meeting rules' cards and the "Add a rule" dialog) ────────────────────────

  // Drawn, g1, g2 and m1 (RC9-COPY1's approved phrase, "written consent of the directors").
  wayLabel: (way: string): string => {
    switch (way) {
      case 'written_consent':
        return 'Written consent of the directors';
      case 'next_meeting':
        // Drawn, g2 (Jonathan's own words).
        return 'A vote at the next meeting';
      case 'officer':
        // Drawn, g2 (Jonathan's own words).
        return 'An officer alone';
      case 'same_way':
        // Drawn, g1 and g3.
        return 'The same way as your other minutes';
      case 'later_meeting':
        // Not drawn: the closed rule's second way in this cut (the orchestrator's C1; the
        // derivation's §2.1 label).
        return 'A vote at a later meeting';
      case 'directors_present':
        // Drawn, g3 and g4: CBG-RC10's way for a closed session's minutes (CBG-RC10-COPY1).
        return 'The signatures of the directors who were there';
      default:
        // Not drawn: a way this build doesn't know, shown as served.
        return way;
    }
  },

  // ── The minutes page: the way's one plain line, and its "When and why" ─────────────────────────

  // Drawn, m1–m7 (Board's `HowThisWorks` label).
  whenAndWhy: 'When and why',

  // Drawn, m1 and m6.
  writtenConsentLine:
    'Your board approves its minutes by written consent of the directors: every director signs.',
  /**
   * Drawn, m1 (no recusal) and m6b (a recusal recorded): every director signs, the conflicted
   * director included, and the record is approved, not the decision. "Givmo starts from…" only while
   * the board's rule isn't recorded (`rule_recorded`).
   */
  writtenConsentWhy: (input: { recusedNames: readonly string[]; ruleRecorded: boolean }) => {
    const givmo = input.ruleRecorded ? [] : [GIVMO_STARTS_FROM];
    if (input.recusedNames.length === 0) {
      // Drawn, m1.
      return [
        ...givmo,
        'Every director signs one consent that names these minutes by their fingerprint. When the last director signs, they’re approved.',
        RECORD_NOT_DECISIONS,
      ];
    }
    const several = input.recusedNames.length > 1;
    return [
      // Drawn, m6b, with the full name (C8).
      `Every director signs one consent that names these minutes by their fingerprint, ${joinWords(input.recusedNames)} included. When the last director signs, they’re approved.`,
      several
        ? // Not drawn: more than one recusal recorded.
          'Approving minutes approves the record of what happened, the recusals included. It doesn’t approve those decisions again: the directors without a conflict decided them, and their records show that.'
        : // Drawn, m6b.
          'Approving minutes approves the record of what happened, the recusal included. It doesn’t approve the decision again: the directors without the conflict decided it, and its record shows that.',
      ...givmo,
    ];
  },

  // Drawn, m4.
  nextMeetingLine: 'Your board approves its minutes by a vote at its next meeting.',
  // Drawn, m4b. "…the person who presided": approved in CBG-RC11-COPY3 (Jonathan, 2026-10-14 10:56
  // PDT), since someone who isn't a director may preside.
  nextMeetingWhy: [
    'Use this when your board’s rules have it approve each meeting’s minutes at its next meeting, as boards following Robert’s Rules do.',
    'At that meeting, record a motion to approve these minutes and its vote. When it carries, they’re approved, recorded against the person who presided.',
    RECORD_NOT_DECISIONS,
  ],

  // Not drawn: the closed rule's "a vote at a later meeting" (C1; the derivation's §2.4 words).
  laterMeetingLine: 'Your board approves a closed session’s minutes by a vote at a later meeting.',
  // Not drawn (the derivation's §2.4, with the meeting way's mechanics). "…the person who presided"
  // (CBG-RC11-COPY3, approved 2026-10-14 10:56 PDT).
  laterMeetingWhy: [
    'Use this when your board’s rules have it approve a closed session’s minutes at a later meeting, usually in a closed session of that meeting, as Robert’s Rules describe.',
    'Record the motion there, and its vote. When it carries, they’re approved, recorded against the person who presided.',
    RECORD_NOT_DECISIONS,
  ],

  // Drawn, m5.
  officerLine: 'Your board’s rules let an officer approve its minutes alone.',
  // Drawn, m5b.
  officerWhy: [
    'Use this only when your board’s rules give one officer, usually the secretary, the authority to approve minutes for the board.',
    'Most boards approve their own minutes. With this way, minutes become the board’s record on one officer’s approval, and the record says who approved them.',
  ],

  // Not drawn: a meeting way with no meeting on the calendar after these minutes' meeting (the
  // contract's §A9 table).
  noNextMeeting: 'When your next meeting is on the calendar, record the motion there.',

  // ── The recusal, shown where the minutes are approved (D-CBG-26 as amended, 20:16 PDT) ──────────

  // Drawn, m6 (one name); not drawn, several.
  recordedAsRecused: (names: readonly string[]) =>
    names.length === 1
      ? `These minutes record ${joinWords(names)} as recused from a decision in them.`
      : `These minutes record ${joinWords(names)} as each recused from a decision in them.`,
  // Not drawn: a closed session's own minutes (the derivation's §2.3.1).
  recordedSteppedOut: (names: readonly string[]) =>
    `These minutes record that ${joinWords(names)} stepped out of this session for a conflict of interest.`,
  // Drawn, m6, with the full name (C8: "Theo Barrett signs the consent too."); not drawn, several.
  signsTheConsentToo: (names: readonly string[]) =>
    names.length === 1
      ? `${joinWords(names)} signs the consent too.`
      : `${joinWords(names)} sign the consent too.`,

  // ── After a withdrawn consent: the meeting way beside a new consent (m7) ────────────────────────

  // Drawn, m7.
  withdrawnLine:
    'The written consent on these minutes was withdrawn. Your board can send a new one, or approve them by a vote at its next meeting.',
  /**
   * Drawn, m7 (its "When and why", open). `meeting` is "March 11", or null with none scheduled. m7's
   * line that a recused director "steps out for that vote" was dropped (2026-10-13 21:44 PDT). Both
   * forms say "…the person who presided" (CBG-RC11-COPY3, approved 2026-10-14 10:56 PDT).
   */
  withdrawnWhy: (input: { meeting: string | null }) => [
    // Drawn, m7.
    // "always" dropped (approved 2026-10-13 22:40 PDT).
    'A board can approve its minutes by a vote at a meeting. Use this when a written consent on them can’t complete, for instance because a director won’t sign.',
    input.meeting !== null
      ? // Drawn, m7.
        `At the ${input.meeting} meeting, record a motion to approve these minutes and its vote. When it carries, they’re approved, recorded against the person who presided.`
      : // Not drawn: no meeting on the calendar yet.
        'At your next meeting, record a motion to approve these minutes and its vote. When it carries, they’re approved, recorded against the person who presided.',
    RECORD_NOT_DECISIONS,
  ],

  // ── The actions ─────────────────────────────────────────────────────────────────────────────────

  // Drawn, m4 and m7 ("Approve them at the March 11 meeting").
  approveAtMeeting: (meeting: string) => `Approve them at the ${meeting} meeting`,
  // Not drawn (C6): a motion approving these minutes is already recorded; with no date read for its
  // meeting, "at a later meeting".
  pendingMotionLine: (meeting: string | null) =>
    meeting !== null
      ? `A motion to approve these minutes is recorded at the ${meeting} meeting.`
      : 'A motion to approve these minutes is recorded at a later meeting.',
  // Not drawn (C6): the way to that meeting's record.
  openThatMeeting: 'Open that meeting',

  // ── The officer's approval dialog ───────────────────────────────────────────────────────────────

  // Not drawn (the derivation's §2.4; the contract's §A9): it replaces the dialog's paragraph about
  // another door (C18), in the one record-not-decisions line every approval says (21:44 PDT).
  approvalRecordNotDecisions: RECORD_NOT_DECISIONS,

  // ── RC9's consent dialog ────────────────────────────────────────────────────────────────────────

  // Drawn, m2.
  consentRecordNotDecisions: RECORD_NOT_DECISIONS,
  // Not drawn (C11): a consent naming a closed session's own minutes. Changed in CBG-FIX104 (the
  // copy page's row 3.12, approved 2026-10-17 17:42 PDT), by the minutes' body: a committee member
  // who isn't on the board doesn't read the board's consent, and a committee's consent, for its own
  // minutes, is read by its members only.
  consentClosedNote: (committeeMinutes: boolean) =>
    committeeMinutes
      ? 'Everyone on the committee reads this consent. It names these minutes by the closed session’s general purpose only.'
      : 'Everyone serving on the board reads this consent, including officers who aren’t directors. It names these minutes by the closed session’s general purpose only.',
  // Not drawn (approved 2026-10-13 22:40 PDT): RC9's `filledInForYou` for a closed session's own
  // minutes, which the form names by the meeting's date and the session's general purpose, never a
  // title; the rest of the sentence is RC9's.
  consentFilledInClosed:
    'Givmo fills in which minutes, from your records: the meeting’s date, the session’s general purpose, the record and its fingerprint. Keep the fingerprint as it is — it’s how the signatures name these exact minutes, and the consent can’t go out to the board without it. The terms in brackets are yours to fill in.',

  // ── Recording the motion at the meeting (the motion dialog's approval mode, C5) ─────────────────

  // Not drawn (C5): the row naming the minutes the motion approves.
  motionApproves: 'Approves',
  // Not drawn (C5): the motion's words, prefilled; the officer may reword them.
  motionText: (minutes: string) => `That ${minutes} be approved.`,
  // Not drawn (C5). Amended in CBG-YR3-ROLES-PRESIDING-COPY1 (Jonathan, 2026-10-14 09:53 PDT): "the
  // person who presided", since anyone recorded as present may preside, director or not (backend
  // `board_minutes.md` §P18 C5). Shown only.
  motionApprovesNote:
    'When its vote carries, these minutes are approved, recorded against the person who presided.',
  // Not drawn (C5). Required beside the link; the note under "Approves" says why, so the field
  // carries none of its own.
  whoPresided: 'Who presided',

  // ── Meeting rules (Governance) ──────────────────────────────────────────────────────────────────

  // Drawn, g1 (the part's description, widened).
  rulesDescription:
    'Your board’s meeting rules — notice period, quorum, the votes needed to pass something, and how minutes are approved — each with the place in your governing documents it comes from.',
  // Drawn, g1–g4 (the two cards' titles, and the dialog's two rules).
  minutesRuleTitle: 'Approving minutes',
  // Drawn, g1–g4.
  closedRuleTitle: 'Approving a closed session’s minutes',
  // Drawn, g1 (to someone who can record a rule).
  givmoStartsHere: 'Givmo starts from this until you record your board’s own rule.',
  // Not drawn: the same, to a reader who can't record one (who entering a rule is said only to
  // someone who can, as the page's own intro does).
  givmoStartsHereReader: 'Givmo starts from this until your board’s own rule is recorded.',
  // Drawn, g4.
  fromYourDocuments: (citation: string) => `From your governing documents: ${citation}`,
  /**
   * The closed card's "When and why" while it says the same way (g1). Its last line names the way this
   * cut offers ("a vote at a later meeting"), where the frame named the signatures of the directors who
   * were there, RC10's (C1).
   */
  closedSameWayWhy: (minutesWayIsWrittenConsent: boolean) => [
    minutesWayIsWrittenConsent
      ? // Drawn, g1.
        'A closed session’s minutes are approved the way your board approves its minutes, by written consent of the directors unless you record another way.'
      : // Not drawn: a board whose minutes rule isn't written consent.
        'A closed session’s minutes are approved the way your board approves its other minutes.',
    // Drawn, g1.
    'Every director can open a closed session’s minutes; they’re sent to the people who were in it.',
    // Changed from g1 (C1): the frame named "the signatures of the directors who were there".
    'If your board’s rules approve them another way, such as a vote at a later meeting, record it here.',
  ],

  // ── "Add a rule": each way, explained at the moment of choosing (g2, g3) ────────────────────────

  // Drawn, g2 and g3 (each way's description).
  wayDescription: (way: RuleWay): string => {
    switch (way) {
      case 'written_consent':
        // Drawn, g2.
        return 'Every director signs one consent naming the minutes. Givmo starts from this way until you record your board’s own.';
      case 'next_meeting':
        // Drawn, g2.
        return 'When your board’s rules have it approve each meeting’s minutes at its next meeting, as boards following Robert’s Rules do. The motion and its vote are recorded at that meeting.';
      case 'officer':
        // Drawn, g2.
        return 'Only when your board’s rules give one officer, usually the secretary, the authority to approve minutes for the board. Most boards approve their own.';
      case 'same_way':
        // Drawn, g3.
        return 'A closed session’s minutes are approved the way your board approves its minutes. Every director can open them.';
      case 'later_meeting':
        // Not drawn (C1).
        return 'When your board’s rules have it approve a closed session’s minutes at a later meeting, usually in a closed session of that meeting, as Robert’s Rules describe. The motion and its vote are recorded at that meeting.';
      case 'directors_present':
        // Drawn, g3 (CBG-RC10-COPY1).
        return 'When your board has adopted it, in its bylaws or by a resolution. Each director who was in the session signs. It isn’t a written consent of the board, which every director signs.';
    }
  },
  // Drawn, g3 ("Where this comes from"'s note for the closed rule).
  closedRuleCitationNote:
    'The section of your bylaws, or the board resolution, that adopted it. Required — a rule without it isn’t attributable to anything.',

  // ── The director's consent page (d4, d4b) ───────────────────────────────────────────────────────

  // Drawn, d4: every signer's line but a recused one's.
  directorRecordNotDecisions: RECORD_NOT_DECISIONS,
  // Drawn, d4b: the minutes record THIS director's own recusal.
  directorYourRecusal:
    'These minutes record your recusal from a decision in them. Signing approves them as the record of what happened, your recusal included, not the decision.',
  // Not drawn (approved 2026-10-13 22:40 PDT): a closed session's own minutes record THIS director
  // as having stepped out of the session for a conflict.
  directorYourStepOut:
    'These minutes record that you stepped out of this session for a conflict of interest. Signing approves them as the record of what happened, not the decisions in it.',
  // Not drawn: a consent naming a closed session's own minutes (the contract's §A9).
  directorReadClosedMinutes: 'Read the closed session’s minutes',
} as const;

/**
 * A closed session's own minutes, named by public facts only (backend §A7): "the minutes of the closed
 * session held during the January board meeting of January 28, 2027 (Compensation)". The meeting's
 * title as the organization wrote it, its date written out, and the general purpose when one is
 * recorded — never the session's own title or anything said in it. Not drawn (the contract's §A9
 * words); its forms with no title or no date, which a read that failed or is under way reaches, were
 * approved in copy round CBG-RC11-COPY2.
 */
export function closedMinutesOfTheMeeting(
  meetingTitle: string | null | undefined,
  meetingDate: string | null | undefined,
  purpose: string | null | undefined,
  naming: MinutesNaming = {},
): string {
  const title = (meetingTitle ?? '').trim();
  const date = namedMeetingDate(meetingDate, naming);
  const named =
    title.length > 0 && date !== null
      ? `the minutes of the closed session held during the ${title} of ${date}`
      : date !== null
        ? // Approved in CBG-RC11-COPY2: the meeting's title not read.
          `the minutes of the closed session held during the meeting of ${date}`
        : // Approved in CBG-RC11-COPY2: neither read.
          'the minutes of a closed session';
  return withPurpose(named, purpose);
}

/**
 * The same minutes named on their own, as the "Approves" rows show minutes: "The minutes of the closed
 * session held during the January board meeting, January 28, 2027 (Compensation)". Not drawn; its forms
 * with no title or no date were approved in copy round CBG-RC11-COPY2, as `closedMinutesOfTheMeeting`'s.
 */
export function theClosedMinutesOf(
  meetingTitle: string | null | undefined,
  meetingDate: string | null | undefined,
  purpose: string | null | undefined,
  naming: MinutesNaming = {},
): string {
  const title = (meetingTitle ?? '').trim();
  const date = namedMeetingDate(meetingDate, naming);
  const named =
    title.length > 0 && date !== null
      ? `The minutes of the closed session held during the ${title}, ${date}`
      : title.length > 0
        ? // Approved in CBG-RC11-COPY2: the minutes' date not recorded.
          `The minutes of the closed session held during the ${title}`
        : date !== null
          ? // Approved in CBG-RC11-COPY2: the meeting's title not read.
            `The minutes of the closed session held during the meeting of ${date}`
          : // Approved in CBG-RC11-COPY2: neither read.
            'The minutes of a closed session';
  return withPurpose(named, purpose);
}

function withPurpose(named: string, purpose: string | null | undefined): string {
  const label = (purpose ?? '').trim();
  return label.length > 0 ? `${named} (${label})` : named;
}
